/**
 * Structured client logging with durable backend transport.
 *
 * Keeps the existing logger API working while adding:
 * - client session IDs
 * - correlation IDs
 * - route and user context binding
 * - best-effort batched transport to the backend
 * - privacy-safe sanitization for free-form payloads (length/hash only; no text previews)
 *
 * @module logger
 */

import { sanitizeLogMetadata, type LogContext as SanitizedLogContext } from './loggerSanitize';

export type LogContext = SanitizedLogContext;

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

interface UserLoggingContext {
  userId?: string | null;
  username?: string | null;
  userType?: string | null;
}

interface ClientLogEvent {
  timestamp: string;
  level: LogLevel;
  event: string;
  message: string;
  route?: string;
  correlationId?: string;
  clientSessionId: string;
  simulationSessionId?: string;
  metadata?: LogContext;
}

interface Logger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, error?: unknown, context?: LogContext): void;
  critical(message: string, error?: unknown, context?: LogContext): void;
  event(event: string, context?: LogContext, level?: LogLevel, message?: string): void;
  createCorrelationId(prefix?: string): string;
  getClientSessionId(): string;
  setUserContext(user: UserLoggingContext | null): void;
  setCurrentRoute(route: string | null): void;
  setLastResponseContext(context: { requestId?: string | null; correlationId?: string | null }): void;
}

const CLIENT_SESSION_STORAGE_KEY = 'deutschgenie.clientSessionId';
const CLIENT_LOG_ENDPOINT = '/api/logging/client-events';
const MAX_BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 750;

const importMetaEnv =
  typeof import.meta !== 'undefined' ? (import.meta as ImportMeta & { env?: Record<string, string | boolean> }).env : undefined;
const isBrowser = typeof window !== 'undefined';
const isDevelopment =
  Boolean(importMetaEnv?.DEV) ||
  (!importMetaEnv?.DEV && typeof process !== 'undefined' && process.env.ENVIRONMENT !== 'production');
const isTestEnvironment = typeof process !== 'undefined' && process.env.ENVIRONMENT === 'test';
const shouldTransportLogs = isBrowser && !isTestEnvironment;

let clientSessionIdCache: string | null = null;
let currentRoute: string | null = null;
let lastResponseContext: { requestId?: string | null; correlationId?: string | null } = {};
let boundUserContext: UserLoggingContext | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let transportHooksInitialized = false;
let transportInFlight = false;
const pendingEvents: ClientLogEvent[] = [];
const RESERVED_METADATA_KEYS = new Set([
  'event',
  'level',
  'message',
  'route',
  'correlationId',
  'clientSessionId',
  'simulationSessionId',
  'requestId',
]);

function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 120) || 'client.log';
}

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function sanitizeContext(context?: LogContext): LogContext | undefined {
  return sanitizeLogMetadata(context, { includeErrorStack: isDevelopment });
}

function omitReservedMetadataKeys(context?: LogContext): LogContext | undefined {
  if (!context) {
    return undefined;
  }

  const filtered: LogContext = {};
  for (const [key, value] of Object.entries(context)) {
    if (RESERVED_METADATA_KEYS.has(key)) {
      continue;
    }
    filtered[key] = value;
  }

  return Object.keys(filtered).length > 0 ? filtered : undefined;
}

function getBoundUserContext(): LogContext | undefined {
  if (!boundUserContext) {
    return undefined;
  }

  return sanitizeContext({
    userId: boundUserContext.userId,
    username: boundUserContext.username,
    userType: boundUserContext.userType,
    userIdentifier: boundUserContext.username || boundUserContext.userId || undefined,
  });
}

function initializeTransportHooks(): void {
  if (!shouldTransportLogs || transportHooksInitialized || !isBrowser) {
    return;
  }

  const flushOnPageHide = () => {
    void flushPendingEvents('pagehide');
  };

  window.addEventListener('pagehide', flushOnPageHide);
  window.addEventListener('beforeunload', flushOnPageHide);
  transportHooksInitialized = true;
}

function getOrCreateClientSessionId(): string {
  if (clientSessionIdCache) {
    return clientSessionIdCache;
  }

  if (!isBrowser) {
    clientSessionIdCache = generateId('client');
    return clientSessionIdCache;
  }

  const existing = window.sessionStorage.getItem(CLIENT_SESSION_STORAGE_KEY);
  if (existing) {
    clientSessionIdCache = existing;
    return existing;
  }

  const created = generateId('client');
  window.sessionStorage.setItem(CLIENT_SESSION_STORAGE_KEY, created);
  clientSessionIdCache = created;
  return created;
}

function scheduleFlush(delayMs: number = FLUSH_DELAY_MS): void {
  if (!shouldTransportLogs) {
    return;
  }

  initializeTransportHooks();
  if (flushTimer) {
    return;
  }

  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushPendingEvents('timer');
  }, delayMs);
}

async function flushPendingEvents(reason: 'timer' | 'pagehide' | 'priority'): Promise<void> {
  if (!shouldTransportLogs || transportInFlight || pendingEvents.length === 0) {
    return;
  }

  transportInFlight = true;
  const events = pendingEvents.splice(0, MAX_BATCH_SIZE);

  try {
    await fetch(CLIENT_LOG_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Session-ID': getOrCreateClientSessionId(),
      },
      credentials: 'include',
      keepalive: reason !== 'timer',
      body: JSON.stringify({ events }),
    });
  } catch {
    // Best-effort transport: intentionally drop failures to avoid UX impact.
  } finally {
    transportInFlight = false;
    if (pendingEvents.length > 0) {
      scheduleFlush(250);
    }
  }
}

function consoleLog(level: LogLevel, entry: ClientLogEvent): void {
  if (!isDevelopment) {
    return;
  }

  const consoleMethod =
    level === 'warn'
      ? console.warn
      : level === 'error' || level === 'critical'
        ? console.error
        : level === 'debug'
          ? console.debug
          : console.info;

  consoleMethod(
    `[${level.toUpperCase()}] ${entry.event}`,
    {
      message: entry.message,
      route: entry.route,
      correlationId: entry.correlationId,
      clientSessionId: entry.clientSessionId,
      simulationSessionId: entry.simulationSessionId,
      metadata: entry.metadata,
    },
  );
}

function buildEvent(
  level: LogLevel,
  event: string,
  message: string,
  context?: LogContext,
): ClientLogEvent {
  const sanitizedContext = sanitizeContext(omitReservedMetadataKeys(context));
  const metadata = sanitizeContext({
    ...getBoundUserContext(),
    ...lastResponseContext,
    ...sanitizedContext,
  });

  return {
    timestamp: new Date().toISOString(),
    level,
    event,
    message,
    route: currentRoute || undefined,
    correlationId: (sanitizedContext?.correlationId as string | undefined) || lastResponseContext.correlationId || undefined,
    clientSessionId: getOrCreateClientSessionId(),
    simulationSessionId:
      (sanitizedContext?.simulationSessionId as string | undefined) ||
      (sanitizedContext?.sessionId as string | undefined) ||
      undefined,
    metadata,
  };
}

function emit(level: LogLevel, message: string, error?: unknown, context?: LogContext): void {
  const eventFromContext = typeof context?.event === 'string' ? context.event : undefined;
  const event = eventFromContext || `client.${slugify(message)}`;
  const metadata = sanitizeContext({
    ...omitReservedMetadataKeys(context),
    ...(error !== undefined ? { error } : {}),
  });
  const entry = buildEvent(level, event, message, metadata);
  consoleLog(level, entry);

  if (!shouldTransportLogs) {
    return;
  }

  if (level === 'debug') {
    return;
  }

  pendingEvents.push(entry);
  if (pendingEvents.length >= MAX_BATCH_SIZE || level === 'error' || level === 'critical') {
    void flushPendingEvents('priority');
    return;
  }
  scheduleFlush();
}

class LoggerImpl implements Logger {
  debug(message: string, context?: LogContext): void {
    emit('debug', message, undefined, context);
  }

  info(message: string, context?: LogContext): void {
    emit('info', message, undefined, context);
  }

  warn(message: string, context?: LogContext): void {
    emit('warn', message, undefined, context);
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    emit('error', message, error, context);
  }

  critical(message: string, error?: unknown, context?: LogContext): void {
    emit('critical', message, error, context);
  }

  event(event: string, context?: LogContext, level: LogLevel = 'info', message?: string): void {
    emit(level, message || event, undefined, { ...context, event });
  }

  createCorrelationId(prefix: string = 'corr'): string {
    return generateId(prefix);
  }

  getClientSessionId(): string {
    return getOrCreateClientSessionId();
  }

  setUserContext(user: UserLoggingContext | null): void {
    boundUserContext = user;
  }

  setCurrentRoute(route: string | null): void {
    currentRoute = route;
  }

  setLastResponseContext(context: { requestId?: string | null; correlationId?: string | null }): void {
    lastResponseContext = context;
  }
}

export const logger: Logger = new LoggerImpl();

export function createCorrelationId(prefix?: string): string {
  return logger.createCorrelationId(prefix);
}

export function getClientSessionId(): string {
  return logger.getClientSessionId();
}

export { sanitizeLogMetadata } from './loggerSanitize';
